import {
  Button,
  Divider,
  Grid,
  Icons,
  ReactSelectController,
  Text,
  Toast,
} from "@pushpress/shared-components";
import React, { useContext } from "react";
import PlanSelectController from "../../../components/controllers/PlanSelect";
import { Control, useWatch } from "react-hook-form";
import {
  SwitchPlanForm,
  getBillingIntervalType,
  getPlanNumberOfSessionsText,
  getCalendarItemTypesRestrictions,
  getUserSubscriptionBillingIntervalName,
  getPlanLimitQuantityHelperText,
  getEntitlementDescription,
} from "./SwitchPlanForm.utils";
import {
  CurrentUserSubscriptionFragment,
  useGetCalendarItemTypesQuery,
} from "../SwitchPlanDrawer-generated.hooks";
import { formatToUSD } from "../../../utils/misc";
import { AppContext } from "../../../context/app";
import SectionTitleAndDescription from "./components/SectionTitleAndDescription";
import { switchPlanFormStyles } from "./SwitchPlanForm.styles";
import { Entitlement } from "src/apis/subscriptionApi";
import { useGetAppointmentTypeItemQuery } from "../../../pages/Appointments/AppointmentTypeDrawer/AppointmentTypeDrawer-generated.hooks";

type SelectNewPlanStepProps = {
  control: Control<SwitchPlanForm>;
  goToNextStep: (newPlan: SwitchPlanForm["newPlan"]) => void;
  userSubscription: CurrentUserSubscriptionFragment;
};

const EntitlementItem = ({
  userSubscription,
  entitlement,
}: {
  userSubscription: CurrentUserSubscriptionFragment;
  entitlement: Entitlement;
}) => {
  const { data: appointmentTypeData } = useGetAppointmentTypeItemQuery({
    variables: {
      uuid: entitlement.refEntitlementUuid,
    },
    skip: !entitlement.refEntitlementUuid || entitlement.entitlementType !== "APPOINTMENT_TYPE",
  });

  if (entitlement.entitlementType !== "APPOINTMENT_TYPE") {
    return null;
  }

  return (
    <>
      <Divider vPadding={12} />
      <Grid item xs={12}>
        <Grid container item xs={12} alignItems="center">
          <Text variant="body-lg" color="wolf-800" weight="600">
            Appointments
          </Text>
          <span
            className="ml-3 mr-3"
            style={switchPlanFormStyles.separatorBullet}
          >
            &#8226;
          </span>
          <Text variant="body-lg" color="wolf-800">
            {entitlement.quantity}x /{" "}
            {getUserSubscriptionBillingIntervalName(
              userSubscription,
            )}
          </Text>
        </Grid>
        <Grid item>
          <Text
            variant="body-lg"
            color="wolf-600"
            className="mt-2"
          >
            {getEntitlementDescription(entitlement, appointmentTypeData?.getAppointmentTypeItem ? { name: appointmentTypeData.getAppointmentTypeItem.name } as any : undefined)}
          </Text>
        </Grid>
      </Grid>
    </>
  );
};

function SelectNewPlanStep({
  control,
  goToNextStep,
  userSubscription,
}: SelectNewPlanStepProps) {
  const { client } = useContext(AppContext);
  const newPlan = useWatch({ control: control, name: "newPlan" });
  const { data: getCalendarItemTypesQuery } = useGetCalendarItemTypesQuery({
    variables: {
      active: true,
    },
  });

  const planIsFull =
    newPlan && newPlan.limitQuantity && newPlan.quantityAvailable === 0;

  return (
    <>
      <Grid container>
        <SectionTitleAndDescription
          title="Current plan"
          description={`
            ${userSubscription.plan.name} (${formatToUSD(
              userSubscription.amount,
              client.currencyIso,
            )} ${getBillingIntervalType(
              userSubscription.intervalType,
              userSubscription.chargeInterval,
            )})
          `}
        />
      </Grid>
      <Divider vPadding={16} />
      <Grid container>
        <Grid item xs={12}>
          <Text
            variant="heading-5"
            color="wolf-800"
            weight="600"
            className="pb-3"
          >
            Choose new plan
          </Text>
          <PlanSelectController
            control={control}
            required
            hideLabel
            label="New plan"
            name="newPlan"
            filterOption={(option) =>
              option.data.type === "recurring" &&
              userSubscription.plan.uuid !== option.data.planUuid
            }
            helperText={getPlanLimitQuantityHelperText(newPlan)}
          />
          {planIsFull && (
            <div className="mt-3 mb-3">
              <Toast
                icon={Icons.Warning}
                message="You must edit the quantity limit of this plan before switching."
                iconColor="warning"
              />
            </div>
          )}
        </Grid>
        { newPlan && newPlan.prices && newPlan.prices.length > 0 && (
          <Grid item xs={12}>
            <Text
              variant="heading-5"
              color="wolf-800"
              weight="600"
              className="pb-3"
            >
              Choose a price
            </Text>
            <ReactSelectController
              placeholder="— Select —"
              name="planPrice"
              menuPortalTarget={document.body}
              getOptionLabel={(option) => formatToUSD(Number(option.amount), client.currencyIso)}
              getOptionValue={(option) => option.planPriceUuid}
              control={control}
              label="New plan price"
              hideLabel
              options={newPlan.prices}
            />
          </Grid>
        )}
        {newPlan && getCalendarItemTypesQuery?.getCalendarClassTypes && (
          <Grid container item xs={12}>
            <div className="p-3" style={switchPlanFormStyles.summaryBox}>
              <Grid item xs={12}>
                <Grid container item xs={12} alignItems="center">
                  <Text variant="body-lg" color="wolf-800" weight="600">
                    Classes
                  </Text>
                  <span
                    className="ml-3 mr-3"
                    style={switchPlanFormStyles.separatorBullet}
                  >
                    &#8226;
                  </span>
                  <Text variant="body-lg" color="wolf-800">
                    {getPlanNumberOfSessionsText(newPlan)}
                  </Text>
                </Grid>
                <Grid item>
                  <Text variant="body-lg" color="wolf-600" className="mt-2">
                    {getCalendarItemTypesRestrictions(
                      newPlan,
                      getCalendarItemTypesQuery.getCalendarClassTypes,
                    )}
                  </Text>
                </Grid>
              </Grid>
              {newPlan.entitlements && newPlan.entitlements.length > 0 &&
                newPlan.entitlements.map((entitlement) => {
                  return (
                    <EntitlementItem
                      key={entitlement.entitlementUuid}
                      entitlement={entitlement}
                      userSubscription={userSubscription}
                    />
                  );
                })}
            </div>
          </Grid>
        )}
      </Grid>
      <Grid container item xs={12}>
        <Divider vPadding={16} />
      </Grid>
      <Grid container justifyContent="flex-end">
        <Grid item>
          <Button
            buttonType="secondary"
            text="Next"
            disabled={!newPlan || planIsFull}
            onClick={() => goToNextStep(newPlan)}
          />
        </Grid>
      </Grid>
    </>
  );
}

export default SelectNewPlanStep;
